import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import { useState, useCallback } from 'react';

// Timeline Update Hook
interface TimelineUpdatePayload {
  start_date: string;
  end_date: string;
  time_zone: { value: string; label: string };
}

interface TimelineUpdateVariables {
  assessmentId: string | null;
  payload: TimelineUpdatePayload;
}

const updateTimeline = async ({ assessmentId, payload }: TimelineUpdateVariables) => {
  const response = await apiClient.patch(
    `/assessment/assessments/${assessmentId}/`,
    payload
  );
  return response.data;
};

export const useTimelineUpdate = () => {
  const { openToast } = useToast();
  const queryClient = useQueryClient();
  const [data, setData] = useState<any>(null);
  const [status, setStatus] = useState<number>(0);
  const [error, setError] = useState<any>(null);

  const mutation = useMutation({
    mutationFn: updateTimeline,
    onSuccess: (responseData, variables) => {
      setData(responseData);
      setStatus(200); // Success status
      setError(null);

      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: ['assessment', variables.assessmentId]
      });
      queryClient.invalidateQueries({
        queryKey: ['timeline', variables.assessmentId]
      });

      openToast({
        show: true,
        heading: "Success",
        text: "Timeline updated successfully",
        type: "success",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to update timeline';
      setError(err);
      setStatus(err.response?.status || 0);

      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    }
  });

  // Backwards compatible function
  const submitTimelineUpdate = useCallback(
    async (assessmentId: string | null, payload: TimelineUpdatePayload) => {
      try {
        const result = await mutation.mutateAsync({ assessmentId, payload });
        return result;
      } catch (err) {
      }
    },
    [mutation]
  );

  return {
    // Backwards compatible interface
    data,
    status,
    loading: mutation.isPending,
    error,
    submitTimelineUpdate,
    
    // Additional TanStack Query utilities
    mutation,
    reset: mutation.reset,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};